import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from '../styles/estoques/Dashboard.module.css'
import Header from '../../components/Header';
import Link from '../../components/Link';
import PageTitle from '../../components/PageTitle';
import BarChart from '../../components/BarChart';
import DoughnutChart from '../../components/DoughnutChart';
import useEmpresas from '../../controllers/nomesEmpresas';

const Dashboard = () => {
    const permissoes = JSON.parse(localStorage.getItem('permissoes'));

    const { empresas } = useEmpresas();

    const [empresa, setEmpresa] = useState('');
    const [data, setData] = useState({});

    const [labelFontSize, setLabelFontSize] = useState(window.innerWidth <= 500 ? 10 : 14);
    const [chartHeight, setChartHeight] = useState(window.innerWidth <= 500 ? 200 : 100);

    // Efeito para atualizar o labelFontSize com base no redimensionamento da tela
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 500) {
                setLabelFontSize(10);
                setChartHeight(200);
            } else {
                setLabelFontSize(14);
                setChartHeight(100);
            }
        };

        // Adiciona o event listener de redimensionamento
        window.addEventListener('resize', handleResize);

        // Remove o event listener ao desmontar o componente
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Chaves para os gráficos
    const includeKeysGenero = ['acos', 'banhadas', 'ouros', 'pratas', 'acessorios'];

    // Aços
    const includeKeysAcoGrupos = ['acosAliancas', 'acosAneis', 'acosBrincos', 'acosChokers', 'acosConjuntos', 'acosGargantilhas', 'acosPingentes', 'acosPulseiras', 'acosTornozeleiras'];
    const includeKeysAcoCategorias = ['acosCristais', 'acosLisas', 'acosMadreperolas', 'acosPedras', 'acosPerolas', 'acosZirconias', 'acosCorrentarias'];

    // Banhadas
    const includeKeysBanhadasGrupos = ['banhadasAneis', 'banhadasBrincos', 'banhadasChokers', 'banhadasConjuntos', 'banhadasGargantilhas', 'banhadasPiercings', 'banhadasPingentes', 'banhadasPulseiras', 'banhadasTornozeleiras'];
    const includeKeysBanhadasBanhos = ['banhadasAU', 'banhadasRB', 'banhadasRN', 'banhadasPL'];
    const includeKeysBanhadasCategorias = ['banhadasCristais', 'banhadasLisas', 'banhadasMadreperolas', 'banhadasPedras', 'banhadasPerolas', 'banhadasZirconias', 'banhadasCorrentarias'];

    // Ouros
    const includeKeysOurosGrupos = ['ourosAliancas', 'ourosAneis', 'ourosBrincos', 'ourosChokers', 'ourosConjuntos', 'ourosFechos', 'ourosGargantilhas', 'ourosPingentes', 'ourosPulseiras', 'ourosTarraxas', 'ourosTornozeleiras'];
    const includeKeysOurosCategorias = ['ourosBx', 'ouros18', 'ouros18Bts'];

    // Pratas
    const includeKeysPratasGrupos = ['pratasAliancas', 'pratasAneis', 'pratasBrincos', 'pratasChokers', 'pratasConjuntos', 'pratasGargantilhas', 'pratasPiercings', 'pratasPingentes', 'pratasPulseiras', 'pratasTornozeleiras'];
    const includeKeysPratasCategorias = ['pratasCristais', 'pratasLisas', 'pratasMadreperolas', 'pratasPedras', 'pratasPerolas', 'pratasZirconias', 'pratasCorrentarias'];

    // Acessórios
    const includeKeysAcessoriosGrupos = ['acessoriosBolsas', 'acessoriosOculos', 'acessoriosBombas', 'acessoriosCanetas', 'acessoriosCarteiras', 'acessoriosCuias', 'acessoriosGuardaChuvas', 'acessoriosLencos', 'acessoriosMaletas', 'acessoriosRelogios'];

    // Mapa de nomes personalizados para os gráficos
    const nameMap = {
        // Aços
        acos: 'Aços',
        acosAliancas: 'Alianças',
        acosAneis: 'Anéis',
        acosBrincos: 'Brincos',
        acosChokers: 'Chokers',
        acosConjuntos: 'Conjuntos',
        acosGargantilhas: 'Gargantilhas',
        acosPingentes: 'Pingentes',
        acosPulseiras: 'Pulseiras',
        acosTornozeleiras: 'Tornozeleiras',
        acosCristais: 'Cristais',
        acosLisas: 'Lisas',
        acosMadreperolas: 'Madrepérolas',
        acosPedras: 'Pedras',
        acosPerolas: 'Pérolas',
        acosZirconias: 'Zircônias',
        acosCorrentarias: 'Correntarias',

        // Banhadas
        banhadas: 'Banhadas',
        banhadasAneis: 'Anéis',
        banhadasBrincos: 'Brincos',
        banhadasChokers: 'Chokers',
        banhadasConjuntos: 'Conjuntos',
        banhadasGargantilhas: 'Gargantilhas',
        banhadasPiercings: 'Piercings',
        banhadasPingentes: 'Pingentes',
        banhadasPulseiras: 'Pulseiras',
        banhadasTornozeleiras: 'Tornozeleiras',
        banhadasAU: 'Ouro',
        banhadasRB: 'Ródio Branco',
        banhadasRN: 'Ródio Negro',
        banhadasPL: 'Prata Leve',
        banhadasCristais: 'Cristais',
        banhadasLisas: 'Lisas',
        banhadasMadreperolas: 'Madrepérolas',
        banhadasPedras: 'Pedras',
        banhadasPerolas: 'Pérolas',
        banhadasZirconias: 'Zircônias',
        banhadasCorrentarias: 'Correntarias',

        // Ouros
        ouros: 'Ouros',
        ourosAliancas: 'Alianças',
        ourosAneis: 'Anéis',
        ourosBrincos: 'Brincos',
        ourosChokers: 'Chokers',
        ourosConjuntos: 'Conjuntos',
        ourosFechos: 'Fechos',
        ourosGargantilhas: 'Gargantilhas',
        ourosPingentes: 'Pingentes',
        ourosPulseiras: 'Pulseiras',
        ourosTarraxas: 'Tarraxas',
        ourosTornozeleiras: 'Tornozeleiras',
        ourosBx: 'Baixo',
        ouros18: '18k',
        ouros18Bts: '18k c/ Bts',

        // Pratas
        pratas: 'Pratas',
        pratasAliancas: 'Alianças',
        pratasAneis: 'Anéis',
        pratasBrincos: 'Brincos',
        pratasChokers: 'Chokers',
        pratasConjuntos: 'Conjuntos',
        pratasGargantilhas: 'Gargantilhas',
        pratasPiercings: 'Piercings',
        pratasPingentes: 'Pingentes',
        pratasPulseiras: 'Pulseiras',
        pratasTornozeleiras: 'Tornozeleiras',
        pratasCristais: 'Cristais',
        pratasLisas: 'Lisas',
        pratasMadreperolas: 'Madrepérolas',
        pratasPedras: 'Pedras',
        pratasPerolas: 'Pérolas',
        pratasZirconias: 'Zircônias',
        pratasCorrentarias: 'Correntarias',

        // Acessórios
        acessorios: 'Acessórios',
        acessoriosBolsas: 'Bolsas',
        acessoriosOculos: 'Óculos',
        acessoriosBombas: 'Bombas',
        acessoriosCanetas: 'Canetas',
        acessoriosCarteiras: 'Carteiras',
        acessoriosCuias: 'Cuias',
        acessoriosGuardaChuvas: 'Guarda-chuvas',
        acessoriosLencos: 'Lenços',
        acessoriosMaletas: 'Maletas',
        acessoriosRelogios: 'Relógios'
    };

    useEffect(() => {
        if (empresa) {
            const fetchData = async () => {
                try {
                    const response = await axios.get(`https://provin.ind.br/api/estoques/dashboard/${empresa}`);
                    setData(response.data);
                } catch (error) {
                    console.error(error);
                }
            };
            fetchData();
        }
    }, [empresa]);

    const handleEmpresaChange = (event) => {
        setEmpresa(event.target.value);
    };

    return (
        <div className={styles.body}>
            <Header />

            <Link redirectTo="/estoques" />

            <PageTitle src="/images/common/dashboard.svg" title="Dashboard de Estoques" />

            {/* Filtro de Empresa */}
            <div className={styles.filtroEmpresa}>
                <label>Empresa</label>
                <select value={empresa} onChange={handleEmpresaChange}>
                    <option value="">Selecione...</option>
                    {permissoes.estoques.empresas_permitidas.map((codigo, index) => (
                        <option key={index} value={codigo}>
                            {empresas[codigo] ? `${codigo} - ${empresas[codigo]}` : codigo}
                        </option>
                    ))}
                </select>
            </div>

            {empresa && (
                // Container principal
                <div className={styles.container}>
                    <div className={styles.info_geral}>
                        {data?.geral?.qtd && (
                            <p><span>Quantidade Total:</span> {data.geral.qtd} peças</p>
                        )}
                    </div>

                    {/* Gráfico de Gênero */}
                    <div className={styles.graficoGenero}>
                        <BarChart
                            data={data}
                            includeKeys={includeKeysGenero}
                            nameMap={nameMap}
                            title=""
                            titleColor="#333"
                            titleFontSize={20}
                            labelColor="#000"
                            labelFontSize={labelFontSize}
                            height={150}
                        />
                    </div>

                    {/* Container de gráficos por grupo-pai */}
                    <div className={styles.containerGraficos}>
                        {data?.acos?.qtd && (
                            <div className={styles.containerGraficosFlex}>
                                <div className={styles.tituloGrupo}>
                                    <h1>Aços</h1>
                                    {data?.acos?.qtd && (
                                        <h5>{data.acos.qtd} itens</h5>
                                    )}
                                </div>

                                {/* Gráfico de grupos */}
                                <div className={styles.barChart}>
                                    <BarChart
                                        data={data}
                                        includeKeys={includeKeysAcoGrupos}
                                        nameMap={nameMap}
                                        horizontal={true}
                                        title="Grupos"
                                        titleColor="#fff"
                                        titleFontSize={16}
                                        labelColor="#fff"
                                        labelFontSize={12}
                                        height={chartHeight}
                                        width={200}
                                    />
                                </div>

                                {/* Gráfico de categorias */}
                                <div className={styles.doughnutCharts}>
                                    <DoughnutChart
                                        data={data}
                                        includeKeys={includeKeysAcoCategorias}
                                        nameMap={nameMap}
                                        title="Categorias"
                                        titleColor="#fff"
                                        titleFontSize={16}
                                        labelColor="#fff"
                                        labelFontSize={12}
                                        height={200}
                                        width={200}
                                    />
                                </div>
                            </div>
                        )}

                        {data?.banhadas?.qtd && (
                            <div className={styles.containerGraficosFlex}>
                                <div className={styles.tituloGrupo}>
                                    <h1>{empresa === "14" ? "Brutos" : "Banhadas"}</h1>
                                    {data?.banhadas?.qtd && (
                                        <h5>{data.banhadas.qtd} itens</h5>
                                    )}
                                </div>

                                {/* Gráfico de grupos */}
                                <div className={styles.barChart}>
                                    <BarChart
                                        data={data}
                                        includeKeys={includeKeysBanhadasGrupos}
                                        nameMap={nameMap}
                                        horizontal={true}
                                        title="Grupos"
                                        titleColor="#fff"
                                        titleFontSize={16}
                                        labelColor="#fff"
                                        labelFontSize={12}
                                        height={chartHeight}
                                        width={200}
                                    />
                                </div>

                                {/* Gráficos de banhos e categorias */}
                                <div className={styles.doughnutCharts}>
                                    <DoughnutChart
                                        data={data}
                                        includeKeys={includeKeysBanhadasBanhos}
                                        nameMap={nameMap}
                                        title="Banhos"
                                        titleColor="#fff"
                                        titleFontSize={16}
                                        labelColor="#fff"
                                        labelFontSize={12}
                                    />

                                    <DoughnutChart
                                        data={data}
                                        includeKeys={includeKeysBanhadasCategorias}
                                        nameMap={nameMap}
                                        title="Categorias"
                                        titleColor="#fff"
                                        titleFontSize={16}
                                        labelColor="#fff"
                                        labelFontSize={12}
                                    />
                                </div>
                            </div>
                        )}

                        {data?.ouros?.qtd && (
                            <div className={styles.containerGraficosFlex}>
                                <div className={styles.tituloGrupo}>
                                    <h1>Ouros</h1>
                                    {data?.ouros?.qtd && (
                                        <h5>{data.ouros.qtd} itens</h5>
                                    )}
                                </div>

                                {/* Gráfico de grupos */}
                                <div className={styles.barChart}>
                                    <BarChart
                                        data={data}
                                        includeKeys={includeKeysOurosGrupos}
                                        nameMap={nameMap}
                                        horizontal={true}
                                        title="Grupos"
                                        titleColor="#fff"
                                        titleFontSize={16}
                                        labelColor="#fff"
                                        labelFontSize={12}
                                        height={chartHeight}
                                        width={200}
                                    />
                                </div>

                                {/* Gráfico de categorias */}
                                <div className={styles.doughnutCharts}>
                                    <DoughnutChart
                                        data={data}
                                        includeKeys={includeKeysOurosCategorias}
                                        nameMap={nameMap}
                                        title="Categorias"
                                        titleColor="#fff"
                                        titleFontSize={16}
                                        labelColor="#fff"
                                        labelFontSize={12}
                                        height={200}
                                        width={200}
                                    />
                                </div>
                            </div>
                        )}

                        {data?.pratas?.qtd && (
                            <div className={styles.containerGraficosFlex}>
                                <div className={styles.tituloGrupo}>
                                    <h1>Pratas</h1>
                                    {data?.pratas?.qtd && (
                                        <h5>{data.pratas.qtd} itens</h5>
                                    )}
                                </div>

                                {/* Gráfico de grupos */}
                                <div className={styles.barChart}>
                                    <BarChart
                                        data={data}
                                        includeKeys={includeKeysPratasGrupos}
                                        nameMap={nameMap}
                                        horizontal={true}
                                        title="Grupos"
                                        titleColor="#fff"
                                        titleFontSize={16}
                                        labelColor="#fff"
                                        labelFontSize={12}
                                        height={chartHeight}
                                        width={200}
                                    />
                                </div>

                                {/* Gráficos de categorias */}
                                <div className={styles.doughnutCharts}>
                                    <DoughnutChart
                                        data={data}
                                        includeKeys={includeKeysPratasCategorias}
                                        nameMap={nameMap}
                                        title="Categorias"
                                        titleColor="#fff"
                                        titleFontSize={16}
                                        labelColor="#fff"
                                        labelFontSize={12}
                                    />
                                </div>
                            </div>
                        )}

                        {data?.acessorios?.qtd && (
                            <div className={styles.containerGraficosFlex}>
                                <div className={styles.tituloGrupo}>
                                    <h1>Acessórios</h1>
                                    {data?.acessorios?.qtd && (
                                        <h5>{data.acessorios.qtd} itens</h5>
                                    )}
                                </div>

                                {/* Gráfico de grupos */}
                                <div className={styles.barChart}>
                                    <BarChart
                                        data={data}
                                        includeKeys={includeKeysAcessoriosGrupos}
                                        nameMap={nameMap}
                                        horizontal={true}
                                        title="Grupos"
                                        titleColor="#fff"
                                        titleFontSize={16}
                                        labelColor="#fff"
                                        labelFontSize={12}
                                        height={chartHeight}
                                        width={200}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;